import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import InquiryForm from '../components/InquiryForm';
import { BROKERAGE, OFFICE } from '../lib/contact';
import './Inquire.css';

const copy = {
  buyer: { eyebrow: 'Buyer inquiry', title: 'Tell us what you are looking for.', intro: 'Share your goals and an advisor can follow up about available residences and next steps.' },
  seller: { eyebrow: 'Seller consultation', title: 'Start a selling conversation.', intro: 'Tell us about your property and goals. An advisor can review your request and follow up.' },
  tour: { eyebrow: 'Tour request', title: 'Request a property tour.', intro: 'Enter the property address and suggest a date and time. Georgia MLS selections are not transferred here, and your request does not reserve or confirm a tour.' },
};

// A link can say what the request is about. The mortgage simulator's "Get
// pre-approved" button uses this: DiamondEcho is not a lender, so the request
// asks an agent for an introduction to one. The message is only a starting
// point, and the visitor can change it before sending.
const topics = {
  'pre-approval': {
    kind: 'buyer',
    eyebrow: 'Mortgage pre-approval',
    title: 'Ask about getting pre-approved.',
    intro: 'DiamondEcho is not a lender, and pre-approval comes from a lender. Send this request and an agent can introduce you to one and explain what they will ask for.',
    message: 'I would like to get pre-approved for a mortgage.',
  },
};

// What the page says while no inquiry service is connected to this build.
const closedCopy = {
  buyer: { title: 'Buyer inquiries are not open yet.', what: 'buyer inquiries' },
  seller: { title: 'Seller consultations are not open yet.', what: 'seller consultation requests' },
  tour: { title: 'Tour requests are not open yet.', what: 'tour requests' },
};

// Requests are delivered by the DiamondEcho service. A build with no service
// address has nowhere to send one, so it must not offer a form.
export const inquiryServiceConfigured = () => Boolean((process.env.REACT_APP_BACKEND_URL || '').trim());

const InquiryClosed = ({ kind }) => (
  <section className="de-inquiry-card de-inquiry-closed" aria-labelledby="inquiry-closed-title">
    <p className="eyebrow">Online requests</p>
    <h2 id="inquiry-closed-title">Online requests are not open yet.</h2>
    <p>
      DiamondEcho is not yet taking {closedCopy[kind].what} through this website, so
      nothing can be sent from this page. No details are collected here.
    </p>
    <h3>Brokerage</h3>
    <p className="de-inquiry-closed__contact">
      {BROKERAGE.name}<br />
      <a href={BROKERAGE.phoneHref}>Office {BROKERAGE.phone}</a>
    </p>
    <h3>Reach DiamondEcho directly</h3>
    <p className="de-inquiry-closed__contact">
      <a href={OFFICE.phoneHref}>Direct {OFFICE.phone}</a><br />
      <a href={OFFICE.emailHref}>{OFFICE.email}</a>
    </p>
    <h3>Georgia office</h3>
    <p>{OFFICE.addressLines[0]}<br />{OFFICE.addressLines[1]}</p>
    <div className="de-inquiry-closed__actions">
      <Link className="mf-btn mf-btn--solid" to="/search">Search Georgia MLS</Link>
      <Link className="mf-btn" to="/investment-calculator">Open Deal Studio</Link>
    </div>
  </section>
);

const Inquire = () => {
  const [params] = useSearchParams();
  const requestedKind = params.get('type');
  const kind = Object.prototype.hasOwnProperty.call(copy, requestedKind) ? requestedKind : 'buyer';
  const open = inquiryServiceConfigured();
  const requestedTopic = params.get('topic');
  const topic = Object.prototype.hasOwnProperty.call(topics, requestedTopic) && topics[requestedTopic].kind === kind ? topics[requestedTopic] : null;
  const openCopy = topic ? { eyebrow: topic.eyebrow, title: topic.title, intro: topic.intro } : copy[kind];
  const content = open ? openCopy : { ...copy[kind], title: closedCopy[kind].title, intro: `This page will take ${closedCopy[kind].what} once online requests open.` };

  return (
    <main className="de-inquiry-page">
      <div className="de-inquiry-wrap">
        <div className="de-inquiry-intro">
          <p className="eyebrow">{content.eyebrow}</p>
          <h1>{content.title}</h1>
          <p>{content.intro}</p>
          <div className="de-inquiry-switch" aria-label="Inquiry type">
            <Link to="/inquire?type=buyer" aria-current={kind === 'buyer' ? 'page' : undefined}>Buying</Link>
            <Link to="/inquire?type=seller" aria-current={kind === 'seller' ? 'page' : undefined}>Selling</Link>
            <Link to="/inquire?type=tour" aria-current={kind === 'tour' ? 'page' : undefined}>Tour request</Link>
          </div>
        </div>
        {open ? <InquiryForm key={`${kind}:${topic ? requestedTopic : ''}`} kind={kind} initialMessage={topic ? topic.message : ''} /> : <InquiryClosed kind={kind} />}
      </div>
    </main>
  );
};

export default Inquire;
